<template>
  <PrimeChip
    :label="props.label"
    :pt="chipPt"
    :unstyled="true"
    :removable="props.removable"
    class="meridia-chip"
    :class="[computedTypeClass]"
  >
    <slot v-if="$slots.default" />
    <template v-slot:icon>
      <span
        class="flex items-center select-none"
        v-bind="getPt('iconWrapper', chipPt)"
      >
        <slot v-if="$slots.customIcon" name="customIcon" />
        <MeridiaIcon
          v-else-if="props.icon"
          :name="props.icon"
          type="material-icons-outlined"
          class="chip-icon"
          v-bind="getPt('icon', chipPt)"
        />
      </span>
    </template>
    <template v-slot:removeicon>
      <MeridiaIcon
        name="close"
        type="material-icons-outlined"
        class="chip-icon cursor-pointer select-none"
        v-bind="getPt('removeIcon', chipPt)"
        @click="emits('removeClicked', $event)"
      />
    </template>
  </PrimeChip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import PrimeChip from 'primevue/chip';
import { usePassThrough } from 'primevue/passthrough';
import MeridiaIcon from 'src/design_system/components/icon/MeridiaIcon.vue';
import { getPt } from 'src/design_system/components/utils';
import { PassThroughMeridiaChip } from './types';

const props = withDefaults(
  defineProps<{
    label: string;
    icon?: string;
    removable?: boolean;
    pt?: PassThroughMeridiaChip;
  }>(),
  {
    removable: true,
  }
);

defineSlots<{
  default: () => any;
  customIcon: () => any;
}>();

const emits = defineEmits<{
  (e: 'removeClicked', event: Event): void;
}>();

const computedTypeClass = computed(() => {
  return {
    default: true,
  };
});

const chipPt = usePassThrough(
  props.pt,
  {},
  { mergeSections: true, mergeProps: true }
) as PassThroughMeridiaChip;
</script>

<style lang="scss" scoped>
.meridia-chip {
  @apply inline-flex items-center gap-2xs py-2xs px-xs radius-full transition-all duration-200;

  &.default {
    @apply bg-neutral-surface-muted text-neutral-text-body text-body-small;

    &:hover {
      @apply bg-neutral-surface-disabled;
    }

    &:active {
      background: linear-gradient(
        0deg,
        rgba(30, 41, 59, 0.1) 0%,
        rgba(30, 41, 59, 0.1) 100%
      );
    }
  }

  .chip-icon {
    @apply icon-xs;
  }
}
</style>
